import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as GET_COMMUNITY_QUERY_KEY } from './useGetCommunity';

export type ApiRequest_Approve_Membership = {
  communityId: number;
  membershipId: number;
};

export type ApiResponse_Approve_Membership = {
  id: number;
  community_id: number;
  user_id: string;
  role: string;
  is_verified: boolean;
  joined_at: string;
  status: string;
};

const METHOD = 'POST';
const ENDPOINT = 'communities/{communityId}/members/{membershipId}/approve';

export const useApproveMembershipRequest = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApiRequest_Approve_Membership) => {
      try {
        const data = await handleApiCall<
          ApiResponse_Approve_Membership,
          Record<string, never>
        >(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            communityId: params.communityId.toString(),
            membershipId: params.membershipId.toString(),
          }),
          {}
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to approve membership request.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (_, variables) => {
      enqueueSnackbar('Membership request approved successfully!', { variant: 'success' });
      
      // Invalidate community data to refresh member list
      queryClient.invalidateQueries({
        queryKey: GET_COMMUNITY_QUERY_KEY({ id: variables.communityId }),
      });
    },
  });
};